import { useEffect, useMemo, useRef, useState } from "react";
import { FaUserCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { CTBProgram, ProgramType } from "../../../utils/api/endpoints/programs/parsePrograms";
import useUserCredentials from "../../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../../utils/api/endpoints/user/credentials";
import PrimaryButton from "../../buttons/PrimaryButton";
import RecentUpdatesRightbar from "./RecentUpdatesRightbar";
import ProgramInformation from "./ProgramInfoSidebar";
import { getProgramOverview } from "../../../utils/api/endpoints/programs/programFullview";
import { fetchReportsInfo } from "../../../utils/api/endpoints/reports/getReport";
import axios from "../../../utils/api/axios";
import DoughnutChart from "../../charts/DoughnutChart";
import TableContainer from "../../table/TableContainer";
import TableRow from "../../table/TableRow";

type UserDetails = {
  username: string;
  profilePicture: string | null;
  about: string | null;
  displayName: string | null;
};

type ReportsSummary = {
  reports_received_90_days: number;
  reports_resolved: number;
  assets_in_scope: number;
} | null;

const formatMonthYear = (dateString: string | undefined) => {
  if (!dateString) return "Unknown Date";
  const date = new Date(dateString);
  return date.toLocaleString("default", { month: "long", year: "numeric" });
};

const formatDateShort = (dateString: string | undefined) => {
  if (!dateString) return "Unknown Date";
  const date = new Date(dateString);
  return date.toLocaleString("default", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
};

const ProgramsRightSidebar = ({
  program,
  topOffset = 0,
  onClose
}: {
  program?: CTBProgram;
  topOffset?: number;
  onClose: () => void;
}) => {
  const navigate = useNavigate();
  const { role } = useUserCredentials();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [reportSummary, setReportSummary] = useState<ReportsSummary>(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [pendingScrollInfo, setPendingScrollInfo] = useState(false);
  const infoSectionRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [reportStates, setReportStates] = useState<Record<string, number>>({});
  const [severities, setSeverities] = useState<number[] | undefined>();
  const [openCount, setOpenCount] = useState<number>(0);
  const [closedCount, setClosedCount] = useState<number>(0);

  // Toggle stats view inside this sidebar when menu triggers it
  useEffect(() => {
    const handler = (e: any) => {
      if (!program || !e?.detail || e.detail.id !== program.id) return;
      setShowStats(true);
    };
    window.addEventListener("program:open-stats", handler as any);
    return () => window.removeEventListener("program:open-stats", handler as any);
  }, [program?.id]);

  // Handle Recent Updates (no scroll)
  useEffect(() => {
    const handler = (e: any) => {
      if (!program || !e?.detail || e.detail.id !== program.id) return;
      setShowStats(false);
      // No scroll for recent updates
    };
    window.addEventListener("program:open-updates", handler as any);
    return () => window.removeEventListener("program:open-updates", handler as any);
  }, [program?.id]);

  // Handle Program Information (with scroll to bottom)
  useEffect(() => {
    const handler = (e: any) => {
      if (!program || !e?.detail || e.detail.id !== program.id) return;
      setShowStats(false);
      setPendingScrollInfo(true);
      // no highlight
    };
    window.addEventListener("program:open-info", handler as any);
    return () => window.removeEventListener("program:open-info", handler as any);
  }, [program?.id]);

  // When requested, scroll smoothly to the bottom of the sidebar
  useEffect(() => {
    if (pendingScrollInfo && !showStats) {
      const scrollToBottom = () => {
        const contEl = containerRef.current as HTMLElement | null;
        if (contEl) {
          // Scroll to the very bottom of the sidebar
          contEl.scrollTo({ 
            top: contEl.scrollHeight, 
            behavior: "smooth" 
          });
          setPendingScrollInfo(false);
        } else {
          // Retry if container not ready
          setTimeout(scrollToBottom, 100);
        }
      };
      // Wait a bit for DOM to be ready
      setTimeout(scrollToBottom, 200);
    }
  }, [pendingScrollInfo, showStats]);

  // Fetch stats when stats view opens (VALID states only)
  useEffect(() => {
    const fetchValidProgramStats = async (id: number) => {
      try {
        const res = await fetchReportsInfo(Number(id));
        const VALID = new Set([
          "triage approved",
          "business review",
          "business approved",
          "awaiting fix",
          "retest requested",
          "retest_requested",
          "closed"
        ]);

        const reports: any[] = Array.isArray((res as any)?.reports)
          ? ((res as any).reports as any[])
          : [];

        const normalize = (v?: string | null) => String(v || "").trim().toLowerCase();
        const validReports = reports.filter(r => VALID.has(normalize(r?.status || r?.state)));

        // State table (valid only) - use retest status when available
        const closedByRetestIdsTable: number[] = Array.isArray((res as any)?.summary?.closed_by_retest_report_ids)
          ? ((res as any).summary.closed_by_retest_report_ids as number[])
          : [];
        const stateCounts: Record<string, number> = {};
        validReports.forEach(r => {
          const rid = Number((r as any)?.id ?? (r as any)?.report_id);
          const rawStatus = String(r?.status || r?.state || "").trim();
          const retestStatus = String(r?.retest_status || "").trim();
          
          // Use retest status if available, otherwise use report status
          // But if report is closed by retest, mark as "Closed"
          let effectiveStatus: string;
          if (rawStatus.toLowerCase() === "closed" || (rid && closedByRetestIdsTable.includes(rid))) {
            effectiveStatus = "Closed";
          } else if (retestStatus && retestStatus !== "") {
            effectiveStatus = retestStatus;
          } else {
            effectiveStatus = rawStatus;
          }
          
          stateCounts[effectiveStatus] = (stateCounts[effectiveStatus] || 0) + 1;
        });
        setReportStates(stateCounts);

        // Open/Close/Total (valid only)
        const closedByRetestIds: number[] = Array.isArray((res as any)?.summary?.closed_by_retest_report_ids)
          ? ((res as any).summary.closed_by_retest_report_ids as number[])
          : [];
        const closed = validReports.filter(r => {
          const rid = Number((r as any)?.id ?? (r as any)?.report_id);
          return (
            normalize(r?.status || r?.state) === "closed" ||
            (rid && closedByRetestIds.includes(rid))
          );
        }).length;
        const open = validReports.length - closed;
        setOpenCount(open);
        setClosedCount(closed);

        // Severities (valid only)
        let critical = 0,
          high = 0,
          medium = 0,
          low = 0;
        validReports.forEach(r => {
          const sev = normalize(r?.severity_category || r?.severity);
          if (sev === "critical") critical++;
          else if (sev === "high") high++;
          else if (sev === "medium") medium++;
          else if (sev === "low") low++;
        });
        setSeverities([critical, high, medium, low]);
      } catch (e) {
        setReportStates({});
        setSeverities(undefined);
        setOpenCount(0);
        setClosedCount(0);
      }
    };
    if (showStats && program?.id) fetchValidProgramStats(Number(program.id));
  }, [showStats, program?.id]);

  // Fetch sidebar data when program changes
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!program?.id) return;
      setLoadingOverview(true);
      try {
        const data = await getProgramOverview(Number(program.id));
        if (!cancelled) setUserDetails(data.user);
      } catch {
        if (!cancelled) setUserDetails(null);
      } finally {
        if (!cancelled) setLoadingOverview(false);
      }

      // Reports summary is not needed for BM/AM roles per existing logic
      if (
        role !== UserRole.BUSINESS_MANAGER &&
        role !== UserRole.ADMIN_MANAGER &&
        program?.id
      ) {
        setLoadingReports(true);
        try {
          const res = await fetchReportsInfo(Number(program.id));
          if (!cancelled) {
            if (res) {
              const VALID = new Set([
                "Triage Approved",
                "Business Review",
                "Business Approved",
                "Awaiting Fix",
                "Retest Requested",
                "retest_requested",
                "retest requested",
                "closed",
                "Closed"
              ]);
              const reports = Array.isArray((res as any).reports)
                ? (res as any).reports
                : [];
              const validReports = reports.filter((r: any) =>
                VALID.has(String(r?.status || r?.state || "").trim())
              );
              const closedCount = validReports.reduce((acc: number, r: any) => {
                const st = String(r?.status || r?.state || "").toLowerCase();
                return acc + (st === "closed" ? 1 : 0);
              }, 0);

              setReportSummary({
                reports_received_90_days: validReports.length,
                reports_resolved: closedCount,
                assets_in_scope: res.summary?.assets_in_scope || 0
              });
            } else {
              setReportSummary(null);
            }
          }
        } catch {
          if (!cancelled) setReportSummary(null);
        } finally {
          if (!cancelled) setLoadingReports(false);
        }
      } else {
        setReportSummary(null);
        setLoadingReports(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [program?.id, role]);

  const containerStyle = useMemo(() => {
    return {
      top: `${topOffset}px`,
      height: `calc(100% - ${topOffset}px)`
    } as React.CSSProperties;
  }, [topOffset]);

  if (!program) return null;

  return (
    <aside
      className="absolute right-0 z-40 w-[360px] md:w-[33.333%] max-w-[90vw] md:max-w-none overflow-y-auto rounded-l-2xl border border-gray-200 bg-white shadow-xl"
      style={containerStyle}
      aria-label="Programs Right Sidebar"
      ref={containerRef}
    >
      <div className="flex items-center justify-between px-8 pt-1 pb-0">
        <div className="flex items-center gap-2">
          <img src="/ctb_logo_1.png" alt="Program" className="h-8 w-8 rounded-lg object-cover" />
        </div>
        <button
          className={`rounded-full p-1 text-slate-500 hover:bg-gray-100 hover:text-slate-700 ${showStats ? 'mt-4' : 'mt-6'}`}
          onClick={() => { setShowStats(false); onClose(); }}
          aria-label="Close sidebar"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      {/* Owner quick info (hidden on stats view) */}
      {!showStats && (
      <div className="px-8 -mt-2">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 overflow-hidden rounded-lg border border-gray-200">
            {userDetails?.profilePicture ? (
              <img
                src={userDetails.profilePicture}
                alt="Owner"
                className="h-full w-full object-cover"
              />
            ) : (
              <FaUserCircle className="text-slate-300 h-full w-full" />
            )}
          </div>
          <div>
            <div className="text-xl font-semibold text-slate-900">
              {program?.title || "Unknown Program"}
            </div>
          </div>
        </div>

        {/* Bio */}
        {userDetails?.about && (
          <p className="mt-2 mb-3 text-sm leading-6 text-slate-700">
            {userDetails.about}
          </p>
        )}
      </div>
      )}

      {/* Content area: default info vs. statistics view */}
      <div className="px-8 mt-1">
        {!showStats && (
          <>
            <h3 className="mb-2 text-lg font-semibold text-blue-600">Targets</h3>
            {Array.isArray(program.targets) && program.targets.length > 0 ? (
              <ul className="space-y-2 text-sm" style={{ color: "#58CC02" }}>
                {program.targets.map((t: any, idx: number) => {
                  const label =
                    (t && (t.targetName || t.name || t.label || t.url)) ||
                    (typeof t === "string" ? t : "");
                  return (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "#58CC02" }} />
                      <span className="truncate" title={label}>
                        {label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-[12px] text-slate-500">No targets available</p>
            )}
            <div className="mt-3 text-sm md:text-base text-slate-700">
              <span className="font-semibold">{program.type}</span> <span className="font-semibold">program</span> launched in {formatMonthYear(program.createdAt)}
            </div>
          </>
        )}

        {showStats && (
          <div className="pb-4">
            <div className="flex items-center justify-between -mt-4">
              <h3 className="mb-2 text-lg font-semibold text-blue-600">Report Severity Overview</h3>
            </div>
            <div className="flex justify-center items-center h-64 py-2">
              {severities && severities.some(v => v > 0) && (
                <DoughnutChart
                  height={120}
                  width={120}
                  labels={["CRITICAL", "HIGH", "MEDIUM", "LOW"]}
                  data={severities}
                  backgroundColors={["#DC2626", "#F59E0B", "#FBBF24", "#22C55E"]}
                  hoverBackgroundColors={["#DC2626", "#F59E0B", "#FBBF24", "#22C55E"]}
                />
              )}
              {(!severities || !severities.some(v => v > 0)) && (
                <div className="text-sm text-slate-500">No data available</div>
              )}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center">
                <div className="text-xs text-slate-500">Open</div>
                <div className="text-xl font-semibold text-slate-900">{openCount}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center">
                <div className="text-xs text-slate-500">Close</div>
                <div className="text-xl font-semibold text-slate-900">{closedCount}</div>
              </div>
              <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 text-center">
                <div className="text-xs text-slate-500">Total</div>
                <div className="text-xl font-semibold text-slate-900">{openCount + closedCount}</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="rounded-lg overflow-hidden border border-gray-200 bg-white">
                <div className="flex items-center justify-between gap-10 px-6 py-3" style={{ backgroundColor: '#010D2C' }}>
                  <span className="text-sm font-semibold" style={{ color: '#58CC02' }}>State</span>
                  <span className="text-sm font-semibold" style={{ color: '#58CC02' }}>Reports</span>
                </div>
                <div className="divide-y divide-gray-200">
                  {Object.entries(reportStates).map(([state, value]) => (
                    <div key={state} className="flex items-center justify-between px-6 py-4">
                      <p className="text-slate-900 capitalize">{state}</p>
                      <p className="text-slate-900">{String(value)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA for researchers (hidden on stats view) */}
      {role === UserRole.RESEARCHER && !showStats && (
        <div className="px-4 pt-3">
          <PrimaryButton
            className="w-full"
            onClick={() => navigate(`/dashboard/reports/new/${program.id}`)}
          >
            Report a Bug!
          </PrimaryButton>
        </div>
      )}

      {/* Recent updates (hidden on stats view) */}
      {!showStats && (
        <div className="px-8 mt-6">
          <RecentUpdatesRightbar
            expanded={true}
            toggleExpanded={() => {}}
            loadingReports={loadingReports}
            reportSummary={reportSummary}
            interactive={false}
          />
        </div>
      )}

      {/* Program information (hidden on stats view) */}
      {!showStats && (
        <div ref={infoSectionRef} className="px-8 mt-6 mb-4">
          <ProgramInformation
            expanded={true}
            toggleExpanded={() => {}}
            program={{
              private: program.private,
              type: program.type,
              testing_type: program.testing_type,
              environment_type: program.environment_type,
              compliance_type: program.compliance_type,
              expected_end_date: program.expected_end_date
            }}
            formatDates={formatDateShort}
          />
        </div>
      )}

      {/* Footer spacing */}
      <div className="h-4" />
    </aside>
  );
};

export default ProgramsRightSidebar;


